import React, {FC} from 'react';

/** Terms of Use English body (i18n: the shell component picks the zh/en content component by language; class names reuse the TermsOfUse__* BEM block) */
const EnContent: FC = () => {
  return (
    <>
      <p className="v3-doc-meta">Effective Date: August 28, 2026</p>
      <p className="v3-doc-meta">Last Updated: August 28, 2026</p>
      <p>Thank you for using the products and services provided by Xiangji!</p>
      <p>
        These Xiangji User Terms of Service (hereinafter referred to as “these Terms”) constitute the agreement entered
        into between you and <strong>[full legal name of the actual operating entity of Xiangji]</strong> (hereinafter
        referred to as “Xiangji”, “we”, or “us”) with respect to the registration, login, and use of Xiangji's related
        products and services.
      </p>
      <p>
        Xiangji primarily provides AI-powered visual content processing and creation services for cross-border
        e-commerce and other business users, including but not limited to image translation, image editing, image
        generation, product scene image generation, product model image generation, product detail image set generation,
        main and secondary product image generation, marketing material generation, and other related services.
      </p>
      <p>
        <strong>
          Please read these Terms carefully and make sure you fully understand them before using Xiangji's services, in
          particular the provisions marked in bold that concern your significant rights and interests, including but not
          limited to the Disclaimer, Limitation of Liability, User Content and Intellectual Property, AI-Generated
          Content, Service Changes, Account Management, Legal Liability, and Dispute Resolution provisions.
        </strong>
      </p>
      <p>
        If you do not agree to any or all of the provisions of these Terms, please do not register, log in, or use
        Xiangji's services.
      </p>
      <p>
        By clicking buttons such as “Agree”, “Register”, “Log In”, “Get Started”, or “Confirm” on the web pages, or by
        actually registering, logging in, purchasing, or using Xiangji's services, you shall be deemed to have read,
        understood, and agreed to accept all of the provisions of these Terms.
      </p>
      <p>
        If you use Xiangji's services on behalf of an enterprise, institution, or other organization, you represent and
        warrant that you have been duly authorized to accept these Terms on behalf of such enterprise, institution, or
        organization, and that such entity shall bear the corresponding responsibilities.
      </p>
      <section>
        <h2>1. Scope of These Terms and Confirmation</h2>
        <h3>Xiangji and Product Services</h3>
        <p>
          In these Terms, “Xiangji” and “the Platform” refer to Suzhou Xiangji Technology Service Co., Ltd. and the
          website, web applications, clients, browser extensions, and other related products and services operated by
          it.
        </p>
        <p>The official website of Xiangji is:</p>
        <p>
          <a href="https://www.xiangjifanyi.com/" rel="noreferrer" target="_blank">
            <strong>https://www.xiangjifanyi.com/</strong>
          </a>
        </p>
        <p>
          The specific services provided shall be subject to the product features, service pages, and related rules
          actually made available by Xiangji.
        </p>
        <h3>Product Services</h3>
        <p>
          “Product Services” refers to the various internet-related and AI-related services provided by Xiangji to
          users, including but not limited to:
        </p>
        <ul>
          <li>Image translation;</li>
          <li>Image editing and retouching;</li>
          <li>Image background processing;</li>
          <li>Product image generation;</li>
          <li>Product scene image generation;</li>
          <li>Product model image generation;</li>
          <li>Main and secondary product image generation;</li>
          <li>Product detail image set generation;</li>
          <li>Product detail page, A+ content, and other content generation;</li>
          <li>Video and visual content processing;</li>
          <li>File and PSD-related processing;</li>
          <li>API and other technical services;</li>
          <li>Other related services to be launched by Xiangji in the future.</li>
        </ul>
        <p>
          <strong>
            The specific content, features, service standards, pricing methods, and usage rules of the services shall be
            subject to the services actually provided by Xiangji and the descriptions on the relevant pages.
          </strong>
        </p>
        <h3>Other Agreements</h3>
        <p>
          These Terms, together with the Xiangji Privacy Policy, the agreements relating to AI creation, the
          Subscription Service Purchase Agreement, the API Service Agreement, and other specific service rules,
          constitute the framework of Xiangji's terms of service.
        </p>
        <p>
          If you use a specific service of Xiangji, you shall also comply with the specific agreement or rules
          applicable to that service.
        </p>
        <p>
          In the event of any inconsistency between a specific agreement and these Terms, the provisions of the specific
          agreement shall prevail with respect to the relevant matters.
        </p>
        <h3>Amendments to These Terms</h3>
        <p>
          <strong>
            In view of the rapid development of internet and artificial intelligence technologies, Xiangji reserves the
            right to amend or supplement these Terms in response to changes in laws and regulations, business
            development, technological upgrades, and service adjustments.
          </strong>
        </p>
        <p>
          In the event of material changes to these Terms, Xiangji will notify you by means of announcements on the
          website, product pages, pop-up windows, in-product notifications, or other reasonable methods.
        </p>
        <p>
          The amended Terms shall take effect on the date of publication or on the effective date specified therein.
        </p>
        <p>
          <strong>
            If you do not agree to the amended Terms, you should stop using the relevant services; if you continue to
            access or use Xiangji's services, you shall be deemed to have accepted the amended Terms.
          </strong>
        </p>
      </section>
      <section>
        <h2>2. Account Registration and Security</h2>
        <h3>Account Registration</h3>
        <p>You may be required to register for or log in to a Xiangji account before using certain Xiangji services.</p>
        <p>
          You shall provide true, accurate, and complete information as required on the registration page, and keep such
          information up to date in a timely manner.
        </p>
        <p>
          <strong>
            You shall not register for or use a Xiangji account by providing false information, impersonating or
            misappropriating another person's identity information, or by other improper means.
          </strong>
        </p>
        <h3>Account Security</h3>
        <p>
          You are responsible for properly safeguarding your account, password, and other identity authentication
          credentials.
        </p>
        <p>You shall be responsible for all operations carried out through your account.</p>
        <p>
          You shall bear any losses arising from the disclosure, misappropriation, or unauthorized use by others of your
          account, password, verification code, or other such information due to your own causes.
        </p>
        <h3>Account Sharing</h3>
        <p>
          Except for features explicitly permitted by Xiangji, such as the Enterprise Edition and multi-user
          collaboration,{' '}
          <strong>
            you shall not provide account access to others by renting, lending, selling, transferring, or sharing
            accounts.
          </strong>
        </p>
        <p>
          If Xiangji discovers that an account exhibits abnormal sharing, bulk usage, malicious registration, abnormal
          API calls, or other conduct that may compromise the security of the Platform, Xiangji reserves the right to
          take measures such as restricting login, restricting features, suspending services, or terminating the
          account.
        </p>
        <h3>Third-Party Accounts</h3>
        <p>
          If you log in to Xiangji through Google, WeChat, or another third-party account, you shall comply with the
          service rules of the relevant third-party platform.
        </p>
        <p>
          Xiangji shall not be liable where you are unable to log in to or use Xiangji's services due to causes
          attributable to the third-party account itself.
        </p>
        <h3>Account Cancellation</h3>
        <p>You may apply to cancel your account through the means provided by Xiangji.</p>
        <p>
          <strong>
            After your account is cancelled, the service benefits, unused credits, historical data, and other content
            associated with that account may be deleted or cease to be valid in accordance with Xiangji's relevant
            rules, except as otherwise provided by laws and regulations or otherwise agreed by the parties.
          </strong>
        </p>
      </section>
      <section>
        <h2>3. Rules of Use</h2>
        <p>
          You undertake to comply, in the course of using Xiangji's services, with the laws and regulations of the
          People's Republic of China and of other applicable countries and regions, as well as public morals and public
          order and good customs.
        </p>
        <p>You shall not use Xiangji's services to engage in any of the following acts:</p>
        <h3>Illegal or Non-Compliant Content</h3>
        <p>You shall not create, generate, upload, publish, disseminate, or store:</p>
        <ul>
          <li>Content that violates laws and regulations;</li>
          <li>Content that endangers national security, public security, or social stability;</li>
          <li>Obscene, pornographic, violent, terrorist, or extremist content;</li>
          <li>Fraud, gambling, pyramid schemes, or other unlawful content;</li>
          <li>Content that insults, defames, or harasses others;</li>
          <li>Other content prohibited by laws and regulations.</li>
        </ul>
        <h3>Infringement of Third-Party Rights</h3>
        <p>
          You shall not use Xiangji's services to infringe upon the lawful rights and interests of any third party,
          including but not limited to:
        </p>
        <ul>
          <li>Copyrights;</li>
          <li>Trademark rights;</li>
          <li>Patent rights;</li>
          <li>Portrait rights;</li>
          <li>Privacy rights;</li>
          <li>Reputation rights;</li>
          <li>Trade secrets;</li>
          <li>Other lawful rights and interests.</li>
        </ul>
        <h3>False and Misleading Content</h3>
        <p>
          You shall not use Xiangji's services to create or disseminate false, fraudulent, or consumer-deceiving
          content, or other content that may harm the lawful rights and interests of others.
        </p>
        <h3>Malicious Use</h3>
        <p>You shall not use programs, scripts, bots, crawlers, or other automated means to:</p>
        <ul>
          <li>Register accounts in bulk;</li>
          <li>Maliciously invoke the services;</li>
          <li>Circumvent usage restrictions;</li>
          <li>Crack or evade the billing mechanism;</li>
          <li>Fraudulently obtain service credits;</li>
          <li>Interfere with the normal operation of the Platform;</li>
          <li>Conduct stress tests or attacks against the Platform.</li>
        </ul>
        <h3>Systems and Technical Protections</h3>
        <p>
          <strong>
            Without Xiangji's prior written permission, you shall not reverse engineer, decompile, disassemble, crack,
            copy, modify, or otherwise technically analyze Xiangji's website, software, interfaces, servers, models, or
            other technical systems.
          </strong>
        </p>
        <p>You shall not:</p>
        <ul>
          <li>Crack the Platform's programs;</li>
          <li>Modify or tamper with Platform features;</li>
          <li>Bypass technical protection measures;</li>
          <li>Access servers without authorization;</li>
          <li>Obtain non-public data;</li>
          <li>Probe for Platform security vulnerabilities and launch attacks;</li>
          <li>Otherwise interfere with the normal operation of the Platform.</li>
        </ul>
        <h3>Other Prohibited Conduct</h3>
        <p>
          You shall not engage in any other conduct that violates laws and regulations, these Terms, or Xiangji's other
          service rules.
        </p>
      </section>
      <section>
        <h2>4. User-Uploaded Content</h2>
        <h3>User Content</h3>
        <p>
          “User Content” refers to the text, images, photographs, videos, audio, product information, design files, PSD
          files, and other content that you upload, submit, input, generate, edit, save, or otherwise provide to Xiangji
          in the course of using Xiangji's services.
        </p>
        <h3>Warranty of Rights</h3>
        <p>
          <strong>
            You shall ensure that the User Content you provide to Xiangji has been lawfully sourced and that you have
            obtained all rights, authorizations, and licenses necessary to use such content.
          </strong>
        </p>
        <p>
          In particular, where content involves third-party likenesses, product images, brand trademarks, fonts, music,
          film and television works, design works, or other such content, you shall ensure that the necessary
          authorizations have been obtained.
        </p>
        <h3>Ownership of User Content</h3>
        <p>
          Except as otherwise provided by laws and regulations or otherwise agreed by the parties, the rights you enjoy
          in User Content that lawfully belongs to you shall not be altered by your use of Xiangji's services.
        </p>
        <p>Xiangji shall not acquire ownership of your User Content gratuitously by virtue of these Terms.</p>
        <h3>License Necessary for the Services</h3>
        <p>
          In order to provide the services to you, you agree to grant Xiangji a{' '}
          <strong>non-exclusive, worldwide, royalty-free license</strong> permitting Xiangji, to the extent necessary to
          provide, maintain, optimize, and improve the relevant services, to store, reproduce, transmit, process,
          analyze, and technically process your User Content.
        </p>
        <p>This license shall remain valid for the period necessary to achieve the foregoing service purposes.</p>
        <h3>Content Removal</h3>
        <p>
          If Xiangji discovers, or receives a report indicating, that content you have uploaded or generated may violate
          laws and regulations, these Terms, or the lawful rights and interests of any third party, Xiangji reserves the
          right to remove or block such content, restrict access to it, or take other reasonable measures in light of
          the actual circumstances.
        </p>
      </section>
      <section>
        <h2>5. Special Provisions on AI-Generated Content</h2>
        <p>
          Some of Xiangji's services are provided on the basis of artificial intelligence models, generative AI
          technologies, machine learning algorithms, and third-party AI infrastructure.
        </p>
        <h3>Inherent Uncertainty of Generated Results</h3>
        <p>
          <strong>
            You understand and agree that AI-generated results are subject to a degree of uncertainty, and that Xiangji
            cannot guarantee that generated results will always be accurate, complete, truthful, lawful, consistent with
            your expectations, or free from similarity to third-party works.
          </strong>
        </p>
        <p>Generated results may exhibit:</p>
        <ul>
          <li>Image content inconsistent with the input;</li>
          <li>Errors in product details;</li>
          <li>Errors in product structure or proportions;</li>
          <li>Errors in text recognition or translation;</li>
          <li>Abnormal generation of people or scenes;</li>
          <li>Factual errors;</li>
          <li>Other issues inherent in AI generation technology itself.</li>
        </ul>
        <h3>Responsibility for Human Review</h3>
        <p>
          <strong>
            You shall conduct a human review of the final content generated, translated, edited, or otherwise processed
            by AI.
          </strong>
        </p>
        <p>
          In particular, with respect to information that may influence consumer decisions—such as product prices,
          dimensions, specifications, materials, functions, performance, certifications, and efficacy—you shall verify
          it yourself before publication, sale, or advertising.
        </p>
        <p>
          You shall bear liability in accordance with the law for any losses or disputes arising from your use of
          AI-generated content directly without review.
        </p>
        <h3>No Guarantee of Uniqueness of Generated Results</h3>
        <p>Identical or similar inputs may generate identical or different results.</p>
        <p>
          Xiangji does not guarantee that AI-generated results are unique, nor does it guarantee that other users will
          not generate content similar or identical to yours.
        </p>
        <h3>Third-Party Models</h3>
        <p>
          Some Xiangji features may use third-party AI models, translation engines, cloud computing services, or other
          third-party technical services.
        </p>
        <p>
          <strong>
            Where the relevant services are affected by failures, upgrades, restrictions, adjustments, suspensions, or
            terminations of third-party models or services, Xiangji will take substitute or restorative measures within
            a reasonable scope, except as otherwise provided by law.
          </strong>
        </p>
        <h3>Commercial Use of AI-Generated Content</h3>
        <p>
          Users shall use AI-generated content in accordance with applicable laws and regulations, the terms of service
          of third-party models, and Xiangji's specific product rules.
        </p>
        <p>
          <strong>
            For content used in scenarios such as commercial publication, advertising placement, or product sales, users
            shall independently confirm that they have a lawful basis for such use and bear the corresponding
            responsibilities for commercial use.
          </strong>
        </p>
      </section>
      <section>
        <h2>6. Intellectual Property</h2>
        <h3>Xiangji's Intellectual Property</h3>
        <p>
          <strong>
            The intellectual property rights in Xiangji's website, software, products, page designs, trademarks, logos,
            text, images, programs, algorithms, models, databases, technical documentation, and other content provided
            by Xiangji belong to Xiangji or the relevant rights holders.
          </strong>
        </p>
        <p>Without the written permission of Xiangji or the relevant rights holders, you shall not:</p>
        <ul>
          <li>Copy;</li>
          <li>Modify;</li>
          <li>Disseminate;</li>
          <li>Sell;</li>
          <li>Rent;</li>
          <li>Reverse engineer;</li>
          <li>Commercially exploit;</li>
          <li>Otherwise infringe the relevant intellectual property rights.</li>
        </ul>
        <h3>User Content</h3>
        <p>Users enjoy the corresponding rights in User Content that lawfully belongs to them.</p>
        <p>
          However, users shall ensure that their use of Xiangji's services does not infringe upon the rights of any
          third party.
        </p>
        <h3>Platform-Generated and Displayed Content</h3>
        <p>
          Xiangji may display sample images, templates, fonts, graphics, trademarks, character likenesses, or other
          materials on product pages, in the help center, in promotional materials, or in other contexts.
        </p>
        <p>
          <strong>
            Except as expressly authorized by Xiangji, you shall not use the foregoing content for commercial purposes.
          </strong>
        </p>
      </section>
      <section>
        <h2>7. Paid Services</h2>
        <h3>Paid Services</h3>
        <p>Some Xiangji products and features are paid services.</p>
        <p>
          If you use a paid service, you shall pay the fees in accordance with the prices, billing methods, and service
          rules displayed on the relevant pages.
        </p>
        <h3>Adjustments to Pricing</h3>
        <p>
          Xiangji reserves the right to adjust pricing standards, plan contents, billing methods, and service credits in
          light of business development, technology costs, and market conditions.
        </p>
        <p>
          <strong>
            Where an adjustment affects service benefits that you have already purchased and that remain within their
            validity period, Xiangji will act in accordance with applicable laws and regulations and the relevant
            purchase agreement.
          </strong>
        </p>
        <h3>Subscription Services</h3>
        <p>
          If you purchase a Xiangji subscription service, you shall also comply with the Xiangji Subscription Service
          Purchase Agreement.
        </p>
        <p>
          Matters concerning the subscription service, such as its price, term, benefits, refunds, and auto-renewal,
          shall be governed by the subscription purchase page and the relevant agreement.
        </p>
        <h3>Free Services</h3>
        <p>Xiangji may provide certain free services, trial credits, or trial features.</p>
        <p>
          <strong>
            Xiangji reserves the right to adjust, suspend, or discontinue free services in light of product operations.
          </strong>
        </p>
      </section>
      <section>
        <h2>8. Changes, Interruptions, and Termination of Services</h2>
        <h3>Service Updates</h3>
        <p>
          In order to improve user experience, refine product features, and ensure service security, Xiangji may update,
          upgrade, optimize, adjust, or migrate its products.
        </p>
        <h3>Service Interruptions</h3>
        <p>
          Temporary service interruptions or unavailability caused by any of the following reasons shall not constitute
          a breach by Xiangji:
        </p>
        <ul>
          <li>System maintenance or upgrades;</li>
          <li>Network failures;</li>
          <li>Telecommunications carrier failures;</li>
          <li>Cloud service failures;</li>
          <li>Failures of third-party AI models or technical services;</li>
          <li>Hacker attacks or cybersecurity incidents;</li>
          <li>Force majeure;</li>
          <li>Other causes beyond Xiangji's reasonable control.</li>
        </ul>
        <h3>Service Termination</h3>
        <p>
          Xiangji reserves the right to suspend or terminate the provision of some or all services to you if any of the
          following circumstances occurs:
        </p>
        <ol>
          <li>You violate laws and regulations;</li>
          <li>You violate these Terms or other Xiangji service rules;</li>
          <li>You provide false, incorrect, or unverifiable information;</li>
          <li>The relevant competent authorities so require;</li>
          <li>For the sake of account, system, or transaction security;</li>
          <li>The service itself needs to be discontinued for business, technical, or other reasons;</li>
          <li>Other reasonable circumstances under which Xiangji deems it necessary to terminate the services.</li>
        </ol>
        <h3>Data Processing</h3>
        <p>
          After the services are terminated, Xiangji may delete or anonymize your data in accordance with laws and
          regulations, the Privacy Policy, and the relevant service rules.
        </p>
        <p>
          <strong>
            You shall back up, on your own, the user data you need to retain long term before the services are
            terminated.
          </strong>
        </p>
      </section>
      <section>
        <h2>9. Disclaimer and Limitation of Liability</h2>
        <h3>Force Majeure</h3>
        <p>
          To the extent permitted by law, Xiangji shall not be liable for breach of contract where the services cannot
          be provided as normal due to force majeure.
        </p>
        <p>Force majeure includes but is not limited to:</p>
        <ul>
          <li>Earthquakes;</li>
          <li>Typhoons;</li>
          <li>Floods;</li>
          <li>Fires;</li>
          <li>Wars;</li>
          <li>Acts of government;</li>
          <li>Major public health events;</li>
          <li>Cyberattacks;</li>
          <li>Large-scale network failures;</li>
          <li>Other events that cannot be reasonably foreseen, avoided, or overcome.</li>
        </ul>
        <h3>Third-Party Services</h3>
        <p>
          Some Xiangji services rely on third-party networks, payment services, cloud computing, AI models, translation
          engines, and other services.
        </p>
        <p>
          <strong>
            For service disruptions caused by failures, adjustments, restrictions, or termination of third-party
            services, Xiangji will use reasonable efforts to assist in resolving them, but shall not be liable for
            losses beyond Xiangji's reasonable control.
          </strong>
        </p>
        <h3>Risks of User Conduct</h3>
        <p>You understand and agree that certain risks are inherent in the use of internet and AI services.</p>
        <p>
          Xiangji shall bear no liability beyond that prescribed by law for information, content, conduct, and risks
          arising from other users, third parties, or the internet environment.
        </p>
        <h3>AI Service Risks</h3>
        <p>
          <strong>
            Xiangji does not guarantee that AI-generated content is absolutely accurate, complete, truthful, reliable,
            or fit for any particular commercial purpose.
          </strong>
        </p>
        <p>Users shall exercise independent judgment and review in light of their actual use scenarios.</p>
        <h3>Indirect Losses</h3>
        <p>
          To the maximum extent permitted by law, Xiangji shall not be liable for any indirect losses, loss of profits,
          loss of goodwill, business interruption losses, or other special losses arising from the use of or inability
          to use Xiangji's services.
        </p>
        <p>
          <strong>
            Where the law requires Xiangji to assume liability, the scope of such liability shall be limited to what is
            prescribed by law.
          </strong>
        </p>
      </section>
      <section>
        <h2>10. Handling of Violations and Legal Liability</h2>
        <h3>Handling of Violations</h3>
        <p>
          If Xiangji discovers, or receives a report or complaint indicating, that you have engaged in conduct violating
          laws and regulations, these Terms, or other service rules, Xiangji reserves the right to take, in light of the
          actual circumstances:
        </p>
        <ul>
          <li>A warning;</li>
          <li>Removal or blocking of the relevant content;</li>
          <li>Restriction of the relevant features;</li>
          <li>Restriction of account access;</li>
          <li>Suspension of services;</li>
          <li>Freezing of the account;</li>
          <li>Termination of the account;</li>
          <li>Other reasonable measures.</li>
        </ul>
        <h3>Account Termination</h3>
        <p>
          <strong>
            If your conduct gravely violates laws and regulations, these Terms, or other platform rules, Xiangji
            reserves the right to suspend or permanently terminate your account and the related services.
          </strong>
        </p>
        <p>
          If the services are terminated due to a violation, you may no longer be able to use the relevant service
          benefits in your account.
        </p>
        <h3>User Indemnification</h3>
        <p>
          <strong>
            If your illegal or non-compliant conduct, infringing acts, or breaches of these Terms cause Xiangji or its
            affiliates or partners to suffer any third-party claims, administrative penalties, litigation, arbitration,
            or other losses, you shall bear the corresponding liability in accordance with the law and compensate
            Xiangji for the reasonable losses thus caused.
          </strong>
        </p>
      </section>
      <section>
        <h2>11. Privacy and Personal Information Protection</h2>
        <p>Xiangji attaches great importance to the protection of your personal information and privacy.</p>
        <p>
          For information on how we collect, use, store, protect, and process your personal information, please refer to
          the Privacy Policy published by Xiangji.
        </p>
        <p>
          <strong>
            You shall carefully read and understand the relevant privacy policy before registering, logging in, or using
            Xiangji's services.
          </strong>
        </p>
        <p>
          Where a specific service involves cross-border data transfers, third-party AI models, or other special data
          processing scenarios, Xiangji will handle such data in accordance with applicable laws and regulations and the
          relevant privacy policy.
        </p>
      </section>
      <section>
        <h2>12. Third-Party Services and Links</h2>
        <p>
          Xiangji's services may contain, or connect to, websites, software, APIs, payment services, AI models, or other
          services provided by third parties.
        </p>
        <p>
          <strong>
            Third-party services are provided independently by the respective third parties, and Xiangji makes no
            warranty regarding the accuracy, reliability, legality, or continued availability of third-party services
            beyond the scope prescribed by law.
          </strong>
        </p>
        <p>
          If you use a third-party service, you shall comply with that third party's terms of service and privacy
          policy.
        </p>
        <p>
          Disputes arising from your use of third-party services shall, in principle, be resolved between you and the
          relevant third party.
        </p>
      </section>
      <section>
        <h2>13. Service Feedback</h2>
        <p>
          If you provide Xiangji with comments, suggestions, product feedback, or other information, you agree that
          Xiangji may, provided that your lawful rights and interests under the law are not infringed, use such feedback
          for product improvement, technical optimization, and service operations.
        </p>
        <p>
          You understand and agree that, unless otherwise agreed in writing by the parties, Xiangji is under no
          obligation to pay you any fee for adopting your comments or suggestions.
        </p>
      </section>
      <section>
        <h2>14. Termination of These Terms</h2>
        <ol>
          <li>
            These Terms take effect on the date on which you register, log in, or first use Xiangji's services, and
            shall remain in effect until you cancel your account or Xiangji ceases to provide services to you.
          </li>
          <li>
            Upon termination of these Terms, those provisions which by their nature should survive termination,
            including but not limited to the provisions on intellectual property, user responsibility, the Disclaimer,
            Limitation of Liability, and dispute resolution, shall remain in effect.
          </li>
        </ol>
      </section>
      <section>
        <h2>15. Governing Law, Jurisdiction, and Miscellaneous</h2>
        <ol>
          <li>
            <strong>
              The laws of the People's Republic of China shall apply to the formation, effectiveness, performance,
              interpretation, amendment, and termination of these Terms, as well as to the resolution of disputes
              arising herefrom.
            </strong>
          </li>
          <li>
            In the event of a dispute arising from these Terms or from your use of Xiangji's services, the parties shall
            first seek to resolve it through friendly negotiation.
          </li>
          <li>
            <strong>
              If negotiation fails, either party may bring a lawsuit before the people's court of competent jurisdiction
              at the locality of the actual operating entity of Xiangji.
            </strong>
          </li>
          <li>
            If any provision of these Terms is held by a court of competent jurisdiction to be invalid, illegal, or
            unenforceable, the validity of the remaining provisions shall not be affected.
          </li>
          <li>
            The headings in these Terms are for convenience of reading only and shall not affect the interpretation or
            legal effect of the provisions.
          </li>
        </ol>
      </section>
      <section>
        <h2>16. Contact Us</h2>
        <p>
          If you have any questions, comments, or complaints regarding these Terms, Xiangji's products, or the related
          services, you may contact us through the following channels:
        </p>
        <ul>
          <li>
            Official website:{' '}
            <a href="https://www.xiangjifanyi.com/" rel="noreferrer" target="_blank">
              https://www.xiangjifanyi.com/
            </a>
          </li>
          <li>
            <strong>Online support:</strong> the in-product online customer support of Xiangji
          </li>
          <li>
            <strong>Support email:</strong> [Official support email to be provided]
          </li>
          <li>
            <strong>Operating entity:</strong> [Full legal name of the actual operating entity to be provided]
          </li>
          <li>
            <strong>Registered address:</strong> [Registered address to be provided]
          </li>
        </ul>
        <p>We will address your inquiries or feedback within a reasonable period after receiving them.</p>
      </section>
    </>
  );
};

export default EnContent;
